import { Categories, CategoryCardInput } from '@/components/ui/Categories';
import { Header } from '@/components/ui/Header';
import { Languages, useLanguage, withLocale } from '@/contexts/LanguageContext';
import { Compact, mergeDeep, replaceWasabiUrl, trimLanguageKeys } from '@/utils';
import { readFileSync } from 'fs';
import compact from 'lodash/compact';
import { GetServerSideProps } from 'next';
import { getLookup } from 'utils/lookupHelpers';
import ErrorPage from 'next/error';
import Head from 'next/head';
import { GetAllDataQuery } from 'queries/getAllData';
import React from 'react';
import { SeoProps } from 'types';

type AllNewsProps = SeoProps & {
  lang: Languages;
  articles: Compact<GetAllDataQuery['allOverviewNewsArticles']>;
};

function AllNewsPage(props: AllNewsProps) {
  const { lang, articles } = props;
  const { t } = useLanguage();

  if (!articles.length) {
    return (
      <>
        <Head>
          <meta name="robots" content="noindex" />
        </Head>
        <ErrorPage statusCode={404} />
      </>
    );
  }

  const categories: Array<CategoryCardInput> = articles.map((article) => {
    return {
      img: article.card_illustration?.media?.url ?? '',
      text: article?.[lang]?.title || '',
      url: `[lang]/news/${article.slug}`,
      scrollToTop: false,
    };
  });

  return (
    <>
      <Header showLogoOnDesktop={true} />
      <div className="relative flex flex-col lg:max-w-xl md:items-start mt-16">
        <h1 className="mb-6 text-xl md:mb-10 md:text-3xl md:leading-tight">
          <strong className="font-bold">{t.general.news}</strong>
          <br />
          <span className="md:text-2xl">{t.general.chooseTopic}</span>
        </h1>
      </div>
      <Categories categories={categories} className="" inactiveCardClassName="opacity-50" activeCardClassName="opacity-100" />
    </>
  );
}

export default withLocale.hoc(AllNewsPage);

export const getServerSideProps: GetServerSideProps<AllNewsProps, { lang: Languages }> = withLocale.getServerSideProps<AllNewsProps>(
  async (strapiLookup, lang) => {
    const newsArticles = compact(strapiLookup.allOverviewNewsArticles);
    const miscMeta = compact(strapiLookup.allFullMiscSites).find((miscSite) => miscSite?.slug.match(/^\/?news\/?$/));
    const seoProps: SeoProps = {
      title: miscMeta?.[lang]?.title,
      seoSnippet: miscMeta?.[lang]?.description,
      keywords: compact(miscMeta?.[lang]?.tags).join(', '),
    };

    return {
      articles: newsArticles,
      ...seoProps,
    };
  },
  getLookup
);
